/**
 * Issues, Troubleshooting & FAQ page for ClipToCAD (QGIS plugin).
 */
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BugReportIcon from '@mui/icons-material/BugReport';
import { HelpOutlineOutlined as HelpOutlineIcon } from '@mui/icons-material';
import GitHubIcon from '@mui/icons-material/GitHub';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PublicSiteChrome, publicPageSx } from '../site/PublicSiteChrome';
import Footer from '../Footer';
import {
  CLIPTOCAD_FAQS,
  CLIPTOCAD_ISSUES_NEW,
  CLIPTOCAD_QGIS_RANGE,
  CLIPTOCAD_VERSION,
} from './cliptocadCopy';

const ClipToCADIssuesPage = () => {
  return (
    <Box sx={publicPageSx}>
      <PublicSiteChrome
        title="ClipToCAD Support"
        subtitle={`QGIS Plugin ${CLIPTOCAD_VERSION} · GeoVera`}
        active="issues"
      />

      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 3 }}>
          <Button
            component={RouterLink}
            to="/qgis-plugins"
            startIcon={<ArrowBackIcon />}
            sx={{
              color: '#94A3B8',
              textTransform: 'none',
              fontWeight: 600,
              justifyContent: 'flex-start',
              '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.08)', color: '#60A5FA' },
            }}
          >
            All QGIS Plugins
          </Button>
          <Button
            component={RouterLink}
            to="/cliptocad"
            sx={{
              color: '#d7f5ad',
              textTransform: 'none',
              fontWeight: 600,
              justifyContent: 'flex-start',
              '&:hover': { bgcolor: 'rgba(215,245,173,0.08)' },
            }}
          >
            ClipToCAD Overview
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
          <Chip
            label={`v${CLIPTOCAD_VERSION}`}
            size="small"
            sx={{
              bgcolor: 'rgba(215,245,173,0.16)',
              color: '#d7f5ad',
              fontWeight: 800,
              fontSize: 11,
            }}
          />
          <Chip
            label={CLIPTOCAD_QGIS_RANGE}
            size="small"
            sx={{
              bgcolor: 'rgba(59, 130, 246, 0.15)',
              color: '#93C5FD',
              fontWeight: 700,
              fontSize: 11,
            }}
          />
        </Stack>

        <Typography
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '2rem', md: '2.6rem' },
            letterSpacing: '-0.02em',
            mb: 1.5,
          }}
        >
          Help, Troubleshooting &amp; Issue Reporting
        </Typography>
        <Typography sx={{ color: '#b8cabd', fontSize: '1.05rem', mb: 5 }}>
          Answers for ClipToCAD {CLIPTOCAD_VERSION}: selection-only copy, Real vs Local, parcel
          labels, install folder names, and AutoCAD paste. Report bugs on GitHub with your QGIS and
          AutoCAD versions.
        </Typography>

        <Box sx={{ mb: 6 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <HelpOutlineIcon sx={{ color: '#d7f5ad', fontSize: 24 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.4rem' }}>
              Frequently Asked Questions
            </Typography>
          </Stack>

          <Stack spacing={2}>
            {CLIPTOCAD_FAQS.map((faq) => (
              <Accordion
                key={faq.q}
                elevation={0}
                sx={{
                  bgcolor: 'rgba(8,23,19,0.75)',
                  border: '1px solid rgba(215,245,173,0.2)',
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#d7f5ad' }} />}
                  sx={{ py: 1 }}
                >
                  <Typography sx={{ fontWeight: 700, color: '#eaf2df', fontSize: '1.02rem' }}>
                    {faq.q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 2.5, px: 2.5 }}>
                  <Typography sx={{ color: '#9fb6a5', fontSize: '0.95rem', lineHeight: 1.65 }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Box>

        <Card
          sx={{
            p: { xs: 3, md: 4 },
            bgcolor: 'rgba(8,23,19,0.9)',
            border: '1px solid rgba(215,245,173,0.3)',
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <BugReportIcon sx={{ color: '#d7f5ad', fontSize: 28 }} />
              <Typography sx={{ fontWeight: 800, fontSize: '1.35rem' }}>
                Report a Bug or Request a Feature
              </Typography>
            </Stack>
            <Typography sx={{ color: '#b8cabd', mb: 3, lineHeight: 1.6 }}>
              Include your QGIS version (3.16–4.x), AutoCAD or LT version, coordinate mode (Real or
              Local), source CRS / Target EPSG, and whether the plugin folder is named exactly
              cliptocad.
            </Typography>
            <Button
              component="a"
              href={CLIPTOCAD_ISSUES_NEW}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<GitHubIcon />}
              sx={{
                py: 1.2,
                px: 3,
                fontWeight: 700,
                bgcolor: '#d7f5ad',
                color: '#122c22',
                '&:hover': { bgcolor: '#f1ffdb' },
              }}
            >
              Open New Issue on GitHub
            </Button>
          </CardContent>
        </Card>
      </Container>

      <Footer />
    </Box>
  );
};

export default ClipToCADIssuesPage;
